import { useEffect, useCallback } from 'react';

/**
 * Custom hook to temporarily override CSS styles of DOM elements
 * @param {boolean} isActive - Boolean indicating whether the style override should be active
 * @param {string} selector - CSS selector for the target element(s) or regex pattern for class names (prefix with 'regex:')
 * @param {Record<string, string>} styles - Object with CSS properties to override
 */
export const useElementStyleOverride = (
  isActive: boolean,
  selector: string,
  styles: Record<string, string>,
) => {
  const applyStyleOverride = useCallback(() => {
    let elements: NodeListOf<Element>;

    // Check if selector is a regex pattern for class names
    if (selector.startsWith('regex:')) {
      const regexPattern = selector.replace('regex:', '');
      const regex = new RegExp(regexPattern);
      const allElements = document.querySelectorAll('*');

      // Filter elements that have classes matching the regex
      const matchingElements: Element[] = [];
      allElements.forEach((element) => {
        const classList = Array.from(element.classList);
        const hasMatchingClass = classList.some((className) =>
          regex.test(className),
        );
        if (hasMatchingClass) {
          matchingElements.push(element);
        }
      });

      // Convert to NodeListOf-like structure
      elements = {
        length: matchingElements.length,
        forEach: (callback: (element: Element, index: number) => void) => {
          matchingElements.forEach(callback);
        },
        item: (index: number) => matchingElements[index] || null,
        [Symbol.iterator]: function* () {
          for (const element of matchingElements) {
            yield element;
          }
        },
      } as unknown as NodeListOf<Element>;
    } else {
      // Use regular CSS selector
      elements = document.querySelectorAll(selector);
    }

    const cleanupFunctions: (() => void)[] = [];

    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      const originalStyles: Record<string, string> = {};

      // Store original values and apply new styles
      Object.keys(styles).forEach((property) => {
        originalStyles[property] = htmlElement.style.getPropertyValue(property);
        htmlElement.style.setProperty(property, styles[property]);
      });

      // Create cleanup function for this element
      cleanupFunctions.push(() => {
        Object.keys(originalStyles).forEach((property) => {
          if (originalStyles[property]) {
            htmlElement.style.setProperty(property, originalStyles[property]);
          } else {
            htmlElement.style.removeProperty(property);
          }
        });
      });
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [selector, styles]);

  useEffect(() => {
    if (isActive) {
      const restoreStyles = applyStyleOverride();
      return restoreStyles;
    }

    return () => {};
  }, [isActive, applyStyleOverride]);
};
